import React, { ReactElement, ReactNode, createContext, useContext, useMemo } from 'react';
import useSWR from 'swr';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { UserWithPermissions } from '../user';
import { Permission } from '../permissions';

export type PermissionObject = { name: string };

export type PermissionsContextValue = {
    permissions: PermissionObject[];
    isLoading: boolean;
    error: Error | undefined;
};

export const buildPermissionsContextValue = (
    permissions: PermissionObject[],
    overrides: Partial<Omit<PermissionsContextValue, 'permissions'>> = {}
): PermissionsContextValue => {
    return {
        permissions,
        isLoading: false,
        error: undefined,
        ...overrides,
    };
};

const DEFAULT_VALUE: PermissionsContextValue = {
    permissions: [],
    isLoading: true,
    error: undefined,
};

export const PermissionsContext = createContext<PermissionsContextValue>(DEFAULT_VALUE);

export const usePermissions = (): PermissionsContextValue => useContext(PermissionsContext);

export const useUserPermissions = <const T extends readonly Permission[]>(
    requested: T
): { userPermissions: { [K in T[number]]: boolean }; isLoading: boolean } => {
    const { permissions, isLoading } = usePermissions();
    const granted = new Set(permissions.map(p => p.name));
    const userPermissions = {} as { [K in T[number]]: boolean };
    for (const perm of requested) {
        userPermissions[perm as T[number]] = granted.has(perm);
    }
    return { userPermissions, isLoading };
};

const fetchPermissions = async ([url, _userUuid]: [string, string]): Promise<PermissionObject[]> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch permissions: ${response.status} ${response.statusText}`);
    }
    return response.json();
};

export const PermissionsProvider = ({
    user,
    children,
}: {
    user: UserWithPermissions | undefined;
    children: ReactNode;
}): ReactElement => {
    const { data, isLoading, error } = useSWR<PermissionObject[], Error>(
        user ? ['/api/user/permissions', user.userUuid] : null,
        fetchPermissions,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60_000,
            onError: err => {
                datadogLogs.logger.error('Could not fetch permissions', { error: err });
                datadogRum.addError(err);
            },
        }
    );

    const value = useMemo<PermissionsContextValue>(() => {
        return {
            permissions: data ?? [],
            isLoading,
            error,
        };
    }, [data, isLoading, error]);

    return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
